import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Job, JobErrors, fetchJobErrors } from '../../services/JobMonitorService';
import { LogLineList } from './LogLineList';
import styles from '../JobsPage.module.css';

interface ErrorsModalProps {
  job: Job;
  token: string;
  onClose: () => void;
}

export const ErrorsModal: React.FC<ErrorsModalProps> = ({ job, token, onClose }) => {
  const [errors, setErrors] = useState<JobErrors | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailure(null);
    try {
      setErrors(await fetchJobErrors(job.job_id, token));
    } catch (e) {
      setFailure((e as Error).message || 'Could not load the errors');
    } finally {
      setLoading(false);
    }
  }, [job.job_id, token]);

  useEffect(() => {
    load();
  }, [load]);

  const lines = errors?.lines ?? [];
  const sources = new Set(lines.map((line) => line.source));

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHead}>
          <div>
            <h3 className={styles.modalTitle}>Errors — {job.display_name}</h3>
            <span className={styles.connState}>
              {errors?.run_id ? `Latest run · ${errors.run_status} · ${errors.run_id}` : 'Latest run'}
            </span>
          </div>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            <X size={14} /> Close
          </button>
        </div>
        <div className={styles.modalBody}>
          {loading && <p className={styles.inlineNote}>Loading…</p>}
          {failure && <div className={styles.errorBar}>{failure}</div>}
          {errors?.run_error && <div className={styles.errorBar}>{errors.run_error}</div>}
          {!loading && !failure && errors?.run_id === null && (
            <p className={styles.inlineNote}>This job has not run yet.</p>
          )}
          {!loading && !failure && errors?.run_id && lines.length === 0 && (
            <p className={styles.inlineNote}>The latest run printed no errors.</p>
          )}
          {lines.length > 0 && (
            <div className={styles.logStream}>
              <LogLineList lines={lines} showSource={sources.size > 1} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
